function removeTrailingCommasFromJson(content) {
    let sanitizedContent = "";
    let isInsideString = false;
    let isEscaped = false;

    for (let i = 0; i < content.length; i++) {
        const char = content[i];

        if (isInsideString) {
            sanitizedContent += char;

            if (isEscaped) {
                isEscaped = false;
                continue;
            }

            if (char === "\\") {
                isEscaped = true;
                continue;
            }

            if (char === "\"") {
                isInsideString = false;
            }

            continue;
        }

        if (char === "\"") {
            isInsideString = true;
            sanitizedContent += char;
            continue;
        }

        if (char === ",") {
            let nextIndex = i + 1;

            while (nextIndex < content.length && /\s/.test(content[nextIndex])) {
                nextIndex++;
            }

            if (content[nextIndex] === "}" || content[nextIndex] === "]") {
                continue;
            }
        }

        sanitizedContent += char;
    }

    return sanitizedContent;
}

function decodeHtmlEntities(content) {
    if (typeof content !== "string" || !content.includes("&")) {
        return content;
    }

    const namedEntities = {
        amp: "&",
        apos: "'",
        gt: ">",
        lt: "<",
        nbsp: " ",
        quot: "\"",
    };

    return content.replace(/&(#\d+|#x[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, entity) => {
        if (entity[0] === "#") {
            const isHex = entity[1]?.toLowerCase() === "x";
            const numericValue = isHex
                ? Number.parseInt(entity.slice(2), 16)
                : Number.parseInt(entity.slice(1), 10);

            if (!Number.isNaN(numericValue)) {
                return String.fromCodePoint(numericValue);
            }

            return match;
        }

        return Object.prototype.hasOwnProperty.call(namedEntities, entity)
            ? namedEntities[entity]
            : match;
    });
}

function isIdentifierBoundary(char) {
    return !char || !/[A-Za-z0-9_$]/.test(char);
}

function matchesKeyword(content, index, keyword) {
    const before = content[index - 1];
    const after = content[index + keyword.length];

    return content.slice(index, index + keyword.length) === keyword
        && isIdentifierBoundary(before)
        && isIdentifierBoundary(after);
}

function readQuotedString(content, startIndex) {
    const quote = content[startIndex];
    let value = "";
    let index = startIndex + 1;

    while (index < content.length) {
        const currentChar = content[index];

        if (currentChar === "\\") {
            const nextChar = content[index + 1];

            if (typeof nextChar === "undefined") {
                value += "\\";
                index += 1;
                continue;
            }

            if (nextChar === "u" && /^[0-9a-fA-F]{4}$/.test(content.slice(index + 2, index + 6))) {
                value += String.fromCharCode(Number.parseInt(content.slice(index + 2, index + 6), 16));
                index += 6;
                continue;
            }

            if (nextChar === "x" && /^[0-9a-fA-F]{2}$/.test(content.slice(index + 2, index + 4))) {
                value += String.fromCharCode(Number.parseInt(content.slice(index + 2, index + 4), 16));
                index += 4;
                continue;
            }

            const escapedChars = {
                "\\": "\\",
                "\"": "\"",
                "'": "'",
                "/": "/",
                b: "\b",
                f: "\f",
                n: "\n",
                r: "\r",
                t: "\t",
                v: "\v",
            };

            value += Object.prototype.hasOwnProperty.call(escapedChars, nextChar)
                ? escapedChars[nextChar]
                : nextChar;
            index += 2;
            continue;
        }

        if (currentChar === quote) {
            return {
                endIndex: index + 1,
                value,
            };
        }

        value += currentChar;
        index += 1;
    }

    throw new Error("JSON inválido: cadena sin cerrar.");
}

function normalizePythonStyleJson(content) {
    if (typeof content !== "string") {
        return content;
    }

    let normalizedContent = "";
    let index = 0;

    while (index < content.length) {
        const currentChar = content[index];

        if (currentChar === "'" || currentChar === "\"") {
            const { endIndex, value } = readQuotedString(content, index);
            normalizedContent += JSON.stringify(value);
            index = endIndex;
            continue;
        }

        if (matchesKeyword(content, index, "None") || matchesKeyword(content, index, "none")) {
            normalizedContent += "null";
            index += 4;
            continue;
        }

        if (matchesKeyword(content, index, "True")) {
            normalizedContent += "true";
            index += 4;
            continue;
        }

        if (matchesKeyword(content, index, "False")) {
            normalizedContent += "false";
            index += 5;
            continue;
        }

        normalizedContent += currentChar;
        index += 1;
    }

    return normalizedContent;
}

function isPlainObject(value) {
    return value && typeof value === "object" && !Array.isArray(value);
}

function looksLikeSalvadoranDte(value) {
    if (!isPlainObject(value) || !isPlainObject(value.identificacion) || !value.emisor || !value.cuerpoDocumento) {
        return false;
    }

    const hasCoreSections = Boolean(value.receptor || value.resumen || value.extension);
    const hasDteType = typeof value.identificacion.tipoDte === "string" || typeof value.identificacion.tipoDte === "number";

    return hasCoreSections && hasDteType;
}

function findSalvadoranDte(value, currentDepth = 0, visited = new WeakSet()) {
    const knownWrapperKeys = [
        "dteJson",
        "DteJson",
        "DTEJson",
        "Documento",
        "documento",
        "json",
        "Json",
        "JSON",
        "data",
        "Data",
        "payload",
        "Payload",
        "body",
        "Body",
        "response",
        "Response",
        "resultado",
        "Resultado"
    ];
    const maxDepth = 6;

    if (!isPlainObject(value) || currentDepth > maxDepth || visited.has(value)) {
        return null;
    }

    visited.add(value);

    if (looksLikeSalvadoranDte(value)) {
        return value;
    }

    for (const key of knownWrapperKeys) {
        if (!Object.prototype.hasOwnProperty.call(value, key)) {
            continue;
        }

        const nestedValue = value[key];

        if (typeof nestedValue === "string") {
            try {
                return parseJsonRelaxed(nestedValue);
            } catch (error) {
                continue;
            }
        }

        const dteFound = findSalvadoranDte(nestedValue, currentDepth + 1, visited);
        if (dteFound) {
            return dteFound;
        }
    }

    for (const nestedValue of Object.values(value)) {
        if (!isPlainObject(nestedValue)) {
            continue;
        }

        const dteFound = findSalvadoranDte(nestedValue, currentDepth + 1, visited);
        if (dteFound) {
            return dteFound;
        }
    }

    return null;
}

function unwrapImportedJsonPayload(parsedContent) {
    return findSalvadoranDte(parsedContent) || parsedContent;
}

function normalizeLookupKey(key) {
    return String(key)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Za-z0-9]/g, "")
        .toLowerCase();
}

const selloRecibidoKeys = new Set([
    "sello",
    "sellorecibido",
    "sellorecepcion",
    "sellorecepcionmh",
    "sellomh",
]);

const responseContainerKeys = new Set([
    "response",
    "responsemh",
    "respuesta",
    "respuestamh",
    "mhresponse",
]);

function hasSelloRecibidoValue(value) {
    return value !== null
        && typeof value !== "undefined"
        && String(value).trim() !== "";
}

const canonicalJsonKeyMap = {
    cuerpodocumento: "cuerpoDocumento",
    codigogeneracion: "codigoGeneracion",
    condicionoperacion: "condicionOperacion",
    complemento: "complemento",
    documentorelacionado: "documentoRelacionado",
    emisor: "emisor",
    fecemi: "fecEmi",
    identificacion: "identificacion",
    ivacomision: "ivaComision",
    ivaperci1: "ivaPerci1",
    ivapercibido: "ivaPercibido",
    ivarete1: "ivaRete1",
    nombrecomercial: "nombreComercial",
    numerocontrol: "numeroControl",
    numerodocumento: "numeroDocumento",
    porcentcomision: "porcentComision",
    preciouni: "precioUni",
    resumen: "resumen",
    sellorecibido: "selloRecibido",
    responsemh: "responseMH",
    respuestamh: "responseMH",
    mhresponse: "responseMH",
    sello: "sello",
    telefono: "telefono",
    tipodte: "tipoDte",
    totalletras: "totalLetras",
    totaldescu: "totalDescu",
    totalivaretenido: "totalIVAretenido",
    totalivaretenidoletras: "totalIVAretenidoLetras",
    totalsujetoretencion: "totalSujetoRetencion",
    totalpagar: "totalPagar",
    totaliva: "totalIva",
    tributos: "tributos",
    subtotal: "subTotal",
    subtotalventas: "subTotalVentas",
    montototaloperacion: "montoTotalOperacion",
    montodescu: "montoDescu",
    montosinpercepcion: "montoSinPercepcion",
    montosujetopercepcion: "montoSujetoPercepcion",
    valoroperaciones: "valorOperaciones",
    liquidoapagar: "liquidoApagar",
    periodoliquidacionfechainicio: "periodoLiquidacionFechaInicio",
    periodoliquidacionfechafin: "periodoLiquidacionFechaFin",
    codliquidacion: "codLiquidacion",
    cantidaddoc: "cantidadDoc",
    nombentrega: "nombEntrega",
    codempleado: "codEmpleado",
    cantidad: "cantidad",
    codigo: "codigo",
    valor: "valor",
    descripcion: "descripcion",
    receptor: "receptor",
    nrc: "nrc",
    nit: "nit",
    nombre: "nombre",
    correo: "correo",
    direccion: "direccion",
    codactividad: "codActividad",
    flete: "flete",
    seguro: "seguro",
};

function getCanonicalJsonKey(key) {
    const normalizedKey = normalizeLookupKey(key);
    return canonicalJsonKeyMap[normalizedKey] || key;
}

function canonicalizeJsonKeysDeep(value) {
    if (Array.isArray(value)) {
        return value.map((item) => canonicalizeJsonKeysDeep(item));
    }

    if (!isPlainObject(value)) {
        return value;
    }

    return Object.entries(value).reduce((accumulator, [key, nestedValue]) => {
        accumulator[getCanonicalJsonKey(key)] = canonicalizeJsonKeysDeep(nestedValue);
        return accumulator;
    }, {});
}

function findValueByNormalizedKeys(value, keys) {
    if (!isPlainObject(value)) {
        return null;
    }

    for (const [key, nestedValue] of Object.entries(value)) {       
        if (keys.has(normalizeLookupKey(key)) && hasSelloRecibidoValue(nestedValue)) {
            return nestedValue;
        }
    }

    return null;
}

function parseNestedObject(value) {
    if (isPlainObject(value)) {
        return value;
    }

    if (typeof value !== "string") {
        return null;
    }

    try {
        const parsedValue = JSON.parse(value);
        return isPlainObject(parsedValue) ? parsedValue : null;
    } catch (error) {
        return null;
    }
}

function findSelloRecibidoRecursive(value, currentDepth = 0, visited = new WeakSet()) {
    const maxDepth = 6;

    if (!isPlainObject(value) || currentDepth > maxDepth || visited.has(value)) {
        return null;
    }

    visited.add(value);

    const directSello = findValueByNormalizedKeys(value, selloRecibidoKeys);
    if (hasSelloRecibidoValue(directSello)) {        
        return directSello;
    }

    for (const [key, nestedValue] of Object.entries(value)) {
        if (!responseContainerKeys.has(normalizeLookupKey(key))) {
            continue;
        }

        const parsedNestedValue = parseNestedObject(nestedValue);
        const nestedSello = findSelloRecibidoRecursive(parsedNestedValue, currentDepth + 1, visited);

        if (hasSelloRecibidoValue(nestedSello)) {
            return nestedSello;
        }
    }

    for (const nestedValue of Object.values(value)) {
        const parsedNestedValue = parseNestedObject(nestedValue);

        if (!parsedNestedValue) {
            continue;
        }

        const nestedSello = findSelloRecibidoRecursive(parsedNestedValue, currentDepth + 1, visited);

        if (hasSelloRecibidoValue(nestedSello)) {
            return nestedSello;
        }
    }

    return null;
}

export function findSelloRecibido(value) {
    return findSelloRecibidoRecursive(value);
}

export function parseJsonRelaxed(content) {
    const normalizedContent = typeof content === "string"
        ? decodeHtmlEntities(content.replace(/^\uFEFF/, ""))
        : content;

    try {
        return unwrapImportedJsonPayload(JSON.parse(normalizedContent));
    } catch (error) {
        const sanitizedContent = removeTrailingCommasFromJson(normalizedContent);

        try {
            return unwrapImportedJsonPayload(JSON.parse(sanitizedContent));
        } catch (sanitizedError) {
            const pythonStyleContent = removeTrailingCommasFromJson(
                normalizePythonStyleJson(normalizedContent)
            );

            if (pythonStyleContent === normalizedContent && sanitizedContent === normalizedContent) {
                throw error;
            }

            return unwrapImportedJsonPayload(JSON.parse(pythonStyleContent));
        }
    }
}

export function parseJsonRelaxedCanonical(content) {
    return canonicalizeJsonKeysDeep(parseJsonRelaxed(content));
}
